import React, { useState } from 'react';
import { Upload, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import QuickAddCustomerButton from '../QuickAddCustomerDialog';

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.heic,.doc,.docx,.xls,.xlsx,.csv,.txt';
const EMPTY = { customer_id: '', customer_name: '', customer_email: '', customer_phone: '', external_rma_number: '', serial_number: '', product_name: '', rma_date: '', completed_date: '', description: '', resolution: '', notes: '' };

const Field = ({ label, k, form, setForm, type = 'text', area, span }) => (
  <div className={span ? 'col-span-2' : ''}>
    <Label>{label}</Label>
    {area
      ? <Textarea rows={2} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-sm" data-testid={`ext-rma-${k}`} />
      : <Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-sm" data-testid={`ext-rma-${k}`} />}
  </div>
);

export const ExternalRmaDialog = ({ api, open, onOpenChange, customers, setCustomers, onSaved }) => {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);

  const reset = () => { setFile(null); setForm(EMPTY); };
  const pickCustomer = (c) => setForm(f => ({ ...f, customer_id: c?.id || '', customer_name: c?.customer_name || f.customer_name, customer_email: c?.email || f.customer_email, customer_phone: c?.phone || f.customer_phone }));

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setParsing(true);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api().post('/warranty-tickets/parse-external', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = r.data || {};
      const match = customers.find(c => d.customer_name && (c.customer_name || '').toLowerCase() === d.customer_name.toLowerCase());
      setForm(prev => ({ ...prev, ...Object.fromEntries(Object.entries(d).filter(([, v]) => v)), customer_id: match?.id || prev.customer_id }));
      toast.success(`Auto-filled from ${f.name}. Please review`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not auto-fill. Please fill the form manually'); }
    finally { setParsing(false); }
  };

  const save = async () => {
    if (!file) { toast.error('Attach the RMA document'); return; }
    if (!form.customer_id && !form.customer_name.trim()) { toast.error('Customer is required'); return; }
    if (!form.description.trim()) { toast.error('Issue description is required'); return; }
    setSaving(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('data', JSON.stringify(form));
      const r = await api().post('/warranty-tickets/external', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.ticket_number} saved as completed External RMA`);
      reset(); onOpenChange(false); onSaved(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="ext-rma-dialog">
        <DialogHeader><DialogTitle>Upload External RMA</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">For RMAs completed outside BOM Maestro. Saved as Completed and marked External.</p>
        <label className="flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-sm p-4 cursor-pointer hover:border-orange-400 hover:bg-orange-50/40 transition-colors" data-testid="ext-rma-dropzone">
          <input type="file" accept={ACCEPT} className="hidden" onChange={onFile} disabled={parsing} data-testid="ext-rma-file-input" />
          {parsing ? <Sparkles className="w-5 h-5 text-orange-500 animate-pulse" /> : <Upload className="w-5 h-5 text-slate-500" />}
          <div className="text-sm">
            <p className="font-medium" data-testid="ext-rma-file-name">{parsing ? 'Reading document with AI…' : (file ? file.name : 'Choose RMA document')}</p>
            <p className="text-xs text-slate-500">PDF, image, Word, Excel, CSV. Fields auto-fill for you to review.</p>
          </div>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Customer *</Label>
            <div className="flex gap-2">
              <select value={form.customer_id} onChange={(e) => pickCustomer(customers.find(c => c.id === e.target.value))} className="flex-1 border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="ext-rma-customer-select">
                <option value="">— pick a customer or type below —</option>
                {customers.map(c => <option key={c.id} value={c.id}>{c.customer_name}</option>)}
              </select>
              <QuickAddCustomerButton api={api} testId="ext-rma-new-customer" onCreated={(c) => { setCustomers(prev => [...prev, c]); pickCustomer(c); }} />
            </div>
          </div>
          <Field label="Customer Name" k="customer_name" form={form} setForm={setForm} />
          <Field label="External RMA #" k="external_rma_number" form={form} setForm={setForm} />
          <Field label="Customer Email" k="customer_email" form={form} setForm={setForm} />
          <Field label="Customer Phone" k="customer_phone" form={form} setForm={setForm} />
          <Field label="Serial #" k="serial_number" form={form} setForm={setForm} />
          <Field label="Product" k="product_name" form={form} setForm={setForm} />
          <Field label="RMA Date" k="rma_date" type="date" form={form} setForm={setForm} />
          <Field label="Completed Date" k="completed_date" type="date" form={form} setForm={setForm} />
          <Field label="Issue *" k="description" area span form={form} setForm={setForm} />
          <Field label="Resolution" k="resolution" area span form={form} setForm={setForm} />
          <Field label="Notes" k="notes" area span form={form} setForm={setForm} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} className="rounded-sm" data-testid="ext-rma-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving || parsing} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="ext-rma-save">{saving ? 'Saving…' : 'Save External RMA'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExternalRmaDialog;
